import { useEffect, useState } from "react";
import { odpiralniCas } from "@/mocks/lokacija";

const JS_DAY_TO_ID = ["ned", "pon", "tor", "sre", "cet", "pet", "sob"];
const ORDER = ["pon", "tor", "sre", "cet", "pet", "sob", "ned"];

const V_DAY: Record<string, string> = {
  pon: "v ponedeljek",
  tor: "v torek",
  sre: "v sredo",
  cet: "v četrtek",
  pet: "v petek",
  sob: "v soboto",
  ned: "v nedeljo",
};

interface ParsedHours {
  openMin: number;
  closeMin: number;
}

function parseHours(ure: string): ParsedHours | null {
  const match = ure.match(/(\d{1,2})\.(\d{2})\s*[–-]\s*(\d{1,2})\.(\d{2})/);
  if (!match) return null;

  return {
    openMin: Number(match[1]) * 60 + Number(match[2]),
    closeMin: Number(match[3]) * 60 + Number(match[4]),
  };
}

function formatMinutes(total: number) {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${String(hours).padStart(2, "0")}.${String(minutes).padStart(2, "0")}`;
}

export interface OpeningStatus {
  todayId: string;
  todayHours: string;
  isOpen: boolean;
  statusText: string;
  nextOpenText: string;
}

function getStatus(date: Date): OpeningStatus {
  const todayId = JS_DAY_TO_ID[date.getDay()];
  const today = odpiralniCas.find((item) => item.id === todayId) ?? odpiralniCas[0];
  const parsed = parseHours(today.ure);
  const nowMinutes = date.getHours() * 60 + date.getMinutes();

  const isOpen = Boolean(
    parsed && nowMinutes >= parsed.openMin && nowMinutes < parsed.closeMin,
  );

  let statusText = "Trenutno zaprto";

  if (parsed && isOpen) {
    statusText = `Odprto do ${formatMinutes(parsed.closeMin)}`;
  }

  const todayIndex = ORDER.indexOf(todayId);
  let nextOpenText = "";

  for (let step = 0; step < 8; step += 1) {
    const candidateId = ORDER[(todayIndex + step + 7) % 7];
    const candidate = odpiralniCas.find((item) => item.id === candidateId);
    const candidateHours = candidate ? parseHours(candidate.ure) : null;

    if (!candidate || !candidateHours) continue;

    if (step === 0 && parsed && nowMinutes < parsed.openMin) {
      nextOpenText = `Odpremo danes ob ${formatMinutes(parsed.openMin)}`;
      break;
    }

    if (step > 0) {
      nextOpenText = `Odpremo ${V_DAY[candidateId]} ob ${formatMinutes(candidateHours.openMin)}`;
      break;
    }
  }

  return {
    todayId,
    todayHours: today.ure,
    isOpen,
    statusText,
    nextOpenText,
  };
}

export function useOpeningStatus(): OpeningStatus {
  const [status, setStatus] = useState<OpeningStatus>(() => getStatus(new Date()));

  useEffect(() => {
    const tick = () => setStatus(getStatus(new Date()));
    tick();
    const timer = window.setInterval(tick, 60000);
    return () => window.clearInterval(timer);
  }, []);

  return status;
}